import { create } from 'zustand';

const COLUMNS_STORAGE_KEY = 'bastion.workflowsList.hiddenColumns';

function loadHiddenColumns(): Set<string> {
  if (typeof window === 'undefined') {
    return new Set();
  }
  try {
    const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(parsed.filter((value): value is string => typeof value === 'string'));
  } catch {
    return new Set();
  }
}

function persistHiddenColumns(ids: Set<string>): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // Quota / private mode — fail silently; the in-memory store stays
    // authoritative for the current session.
  }
}

interface WorkflowsListState {
  /**
   * Group names that the user has explicitly collapsed. Default behavior is
   * "everything expanded" (Linear-style), so the empty set means all groups
   * are visible. Persisting *collapsed* names — instead of expanded — keeps
   * newly fetched groups expanded automatically without needing a seed step.
   */
  collapsedGroups: Set<string>;
  toggleGroup: (name: string) => void;
  /**
   * Column IDs the user has hidden via the Display options menu. Mirrors
   * the same "empty set = all visible" idiom so adding a column later
   * defaults it on. Persisted to localStorage so column choices survive
   * across reloads.
   */
  hiddenColumns: Set<string>;
  toggleColumn: (id: string) => void;
}

export const useWorkflowsListStore = create<WorkflowsListState>((set) => ({
  collapsedGroups: new Set(),
  toggleGroup: (name: string) =>
    set((state) => {
      const next = new Set(state.collapsedGroups);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return { collapsedGroups: next };
    }),
  hiddenColumns: loadHiddenColumns(),
  toggleColumn: (id: string) =>
    set((state) => {
      const next = new Set(state.hiddenColumns);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      persistHiddenColumns(next);
      return { hiddenColumns: next };
    }),
}));
